import { apiSlice } from "../../../features/api/apiSlice";

/**
 * The per-type databank registers — composite rates today, then unit rates,
 * labour rates, plant hire, labour outputs, constants, elemental costs and the
 * benchmarks.
 *
 * Every endpoint takes the register key as `dataset`, so this one file serves
 * all of them; a new register needs a route entry and a menu link, not another
 * api slice.
 *
 * Note there is no `total` and no page number anywhere here. The API pages by
 * CURSOR, because counting rows on a register of this size is the slow query —
 * so the UI walks next/previous and never claims to know how many pages there
 * are. Trading page numbers for a list that stays fast at 100M rows is the
 * whole point.
 */

export type DatasetRow = {
  id: number;
  // Absent on a register that stores the item's text rather than referencing
  // the shared catalogue (plant hire). `description` is what the screen reads
  // either way.
  description_id?: number;
  description: string | null;
  unit_id: number;
  unit: string | null;
  category_id: number;
  category: string | null;
  sub_category_id: number;
  sub_category: string | null;
  // Only registers that file three levels deep send these.
  sub_category_2_id?: number;
  sub_category_2?: string | null;
  // Absent on registers that do not date their entries (labour outputs).
  year?: number | null;
  // Basic price list only: the period goes to the month, and the item carries
  // a size/spec beside its unit of sale.
  month?: number | null;
  month_name?: string | null;
  measurement_id?: number;
  measurement?: string | null;
  // Registers with no location (constants) omit these entirely.
  state_id?: number;
  state?: string | null;
  // Infrastructure benchmarks are published per geopolitical zone (and
  // nationwide), so they carry a second, coarser location beside the state.
  // Either may stand alone — see has_zone. Free text, as the source wrote it.
  zone?: string | null;
  code?: string | null;
  /**
   * The register's numbers, keyed by column. Most registers have one entry
   * ({ rate: 1200 }); constants has four ({ slow, average, fast, other_output }).
   * Look each one up against `meta.values`, which gives the order and labels.
   */
  values: Record<string, number | string | null>;
  is_approved: boolean;
  variant?: string;
};

/** One of a register's numbers: which column, what it is called, how it reads. */
export type DatasetValueSpec = {
  column: string;
  label: string;
  kind: "currency" | "quantity" | "ratio";
};

/**
 * A split WITHIN one register — labour rates is formal and informal. The two
 * differ in how deep they file, what the description and the number are called,
 * and they keep separate category lists, so the screen shows one at a time and
 * every request names which.
 */
export type DatasetVariant = {
  label: string;                    // "Type"
  active: string;                   // "formal"
  options: Array<{ id: number; key: string; label: string }>;
};

export type DatasetMeta = {
  dataset: string;
  label: string;
  // Every number the register carries, in display order. Drives the table
  // headers and the form fields.
  values: DatasetValueSpec[];
  value_label: string;              // the primary number, for anything wanting one
  value_kind: "currency" | "quantity" | "ratio";
  description_label: string;        // "Description", or "Operative / Worker"
  taxonomy_depth: 1 | 2 | 3;        // how many category levels to render
  // Whether the register dates its entries at all. Labour outputs do not, so
  // the screen shows no year column, filter or field for them.
  has_year: boolean;
  has_month: boolean;        // material prices move within a year; rates do not
  has_measurement: boolean;  // 50kg, 12mm — a size, not a unit of sale
  has_state: boolean;        // a constant is not a fact about a place
  // A second location, independent of the state: benchmarks are published by
  // geopolitical zone. Not a replacement for the state — an entry may carry a
  // zone, a state, or both.
  has_zone: boolean;
  has_code: boolean;         // constants are cited by code
  variant: DatasetVariant | null;
  // Pre-computed, whole register (not the active variant), and may lag a
  // recent import — counting is the query this design exists to avoid.
  total_rows: number;
  refreshed_at: string | null;
};

export type DatasetPage = {
  data: DatasetRow[];
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
  meta: DatasetMeta;
};

export type DatasetCategory = {
  id: number;
  name: string;
  children?: DatasetCategory[];     // nested only as deep as the register files
};

export type DatasetFilters = {
  // EVERY state, for the add/edit form — the first entry for a chapter has to
  // be creatable.
  states: Array<{ id: number; name: string }>;
  // The ids the register actually HOLDS, pre-computed on the API side, for the
  // browse filter. Empty means "not known yet" (a stats row older than the
  // column, or a refresh that has not run), NOT "none" — read that way the
  // filter falls back to offering every state.
  states_held: number[];
  // The zones this register actually HOLDS, pre-computed on the API side —
  // free text, so the only honest list is the one in the data. Empty unless the
  // register is zoned (or nothing has been imported into it yet).
  zones: string[];
  units: Array<{ id: number; name: string }>;
  categories: DatasetCategory[];
  years: number[];
  // Empty unless the register uses them.
  months: Array<{ id: number; name: string }>;
  measurements: Array<{ id: number; name: string }>;
  meta: DatasetMeta;
};

type ListArgs = {
  dataset: string;
  admin?: boolean;
  variant?: string;                 // "formal" | "informal" — omitted = default

  state_id?: string | number;
  zone?: string;
  category_id?: string | number;
  sub_category_id?: string | number;
  sub_category_2_id?: string | number;
  unit_id?: string | number;
  measurement_id?: string | number;
  year?: string | number;
  month?: string | number;
  year_from?: string | number;
  year_to?: string | number;
  q?: string;
  sort?: "newest";
  cursor?: string | null;
  per_page?: number;
};

export type DatasetEntryBody = {
  state_id?: number | null;
  zone?: string | null;
  description: string;              // free text — the API catalogues it
  unit?: string | null;             // free text, likewise
  measurement?: string | null;      // free text, likewise ("50kg")
  code?: string | null;
  category_id: number;
  sub_category_id?: number | null;
  sub_category_2_id?: number | null;
  year?: number | null;
  month?: number | null;            // 1–12
  // Keyed by column, the same shape the row comes back in. A single-number
  // register sends { rate: 1200 }; constants sends all four.
  values: Record<string, number | null>;
  is_approved?: boolean;
};

const base = (dataset: string, admin?: boolean) =>
  `/${admin ? "admin/databank" : "databank"}/${dataset}`;

function qs(args: Record<string, any>, keys: string[]) {
  const p = new URLSearchParams();
  keys.forEach((k) => {
    const v = args[k];
    if (v !== undefined && v !== null && String(v) !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

/**
 * Cache identity for a register. A register with variants keeps two entirely
 * separate lists, filter sets and category trees, so the variant is part of the
 * key — otherwise switching tabs serves the other half's rows from cache.
 */
const scopeId = (dataset: string, variant?: string) => `${dataset}:${variant ?? ""}`;

/**
 * Which row of a register's category list a write is addressing.
 *
 * "category" and "sub" are levels, not table names: the API decides from the
 * register whether they are two tables or two depths of one. Defaulting to the
 * category keeps a caller that never passes it working on every register.
 */
export type CategoryLevel = "category" | "sub";

export type CategoryRef = {
  dataset: string;
  variant?: string;
  id: number;
  level?: CategoryLevel;
};

const categoryQuery = (variant?: string, level?: CategoryLevel) => {
  const p = new URLSearchParams();
  if (variant) p.set("variant", variant);
  if (level === "sub") p.set("level", "sub");
  const s = p.toString();
  return s ? `?${s}` : "";
};

const scopeTags = (dataset: string, variant?: string) =>
  [
    { type: "DatasetItems" as const, id: scopeId(dataset, variant) },
    { type: "DatasetFilters" as const, id: scopeId(dataset, variant) },
    { type: "DatasetCategories" as const, id: scopeId(dataset, variant) },
  ];

const LIST_KEYS = [
  "variant",
  "state_id", "zone", "category_id", "sub_category_id", "sub_category_2_id",
  "unit_id", "measurement_id",
  "year", "month", "year_from", "year_to", "q", "sort", "cursor", "per_page",
];

export const datasetApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getDatasetItems: b.query<DatasetPage, ListArgs>({
      query: (a) => `${base(a.dataset, a.admin)}/items${qs(a, LIST_KEYS)}`,
      providesTags: (_r, _e, a) => [{ type: "DatasetItems", id: scopeId(a.dataset, a.variant) }],
    }),

    getDatasetFilters: b.query<DatasetFilters, { dataset: string; admin?: boolean; variant?: string }>({
      query: (a) => `${base(a.dataset, a.admin)}/filters${a.variant ? `?variant=${a.variant}` : ""}`,
      providesTags: (_r, _e, a) => [{ type: "DatasetFilters", id: scopeId(a.dataset, a.variant) }],
    }),

    getDatasetCategories: b.query<DatasetCategory[], { dataset: string; admin?: boolean; variant?: string }>({
      query: (a) => `${base(a.dataset, a.admin)}/categories${a.variant ? `?variant=${a.variant}` : ""}`,
      transformResponse: (r: any) => (Array.isArray(r) ? r : r?.data ?? []),
      providesTags: (_r, _e, a) => [{ type: "DatasetCategories", id: scopeId(a.dataset, a.variant) }],
    }),

    // ── Writes ───────────────────────────────────────────────────────────
    // Every write names the variant it belongs to, and invalidates only that
    // half's cache. Editing an informal daily rate must not make the formal
    // list refetch, and — more to the point — must not let the formal list
    // serve rows that were cached under a tag the write did not touch.

    createDatasetEntry: b.mutation<any, { dataset: string; variant?: string; body: DatasetEntryBody }>({
      query: ({ dataset, variant, body }) => ({
        url: `${base(dataset, true)}/items${variant ? `?variant=${variant}` : ""}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, a) => scopeTags(a.dataset, a.variant),
    }),

    updateDatasetEntry: b.mutation<any, { dataset: string; variant?: string; id: number; body: Partial<DatasetEntryBody> }>({
      query: ({ dataset, variant, id, body }) => ({
        url: `${base(dataset, true)}/items/${id}${variant ? `?variant=${variant}` : ""}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, a) => scopeTags(a.dataset, a.variant),
    }),

    deleteDatasetEntry: b.mutation<any, { dataset: string; variant?: string; id: number }>({
      query: ({ dataset, variant, id }) => ({
        url: `${base(dataset, true)}/items/${id}${variant ? `?variant=${variant}` : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, a) => scopeTags(a.dataset, a.variant),
    }),

    importDataset: b.mutation<any, { dataset: string; variant?: string; file: FormData }>({
      query: ({ dataset, variant, file }) => ({
        url: `${base(dataset, true)}/import${variant ? `?variant=${variant}` : ""}`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: (_r, _e, a) => scopeTags(a.dataset, a.variant),
    }),

    createDatasetCategory: b.mutation<any, { dataset: string; variant?: string; name: string; parent_id?: number }>({
      query: ({ dataset, variant, name, parent_id }) => ({
        url: `${base(dataset, true)}/categories${variant ? `?variant=${variant}` : ""}`,
        method: "POST",
        body: { name, parent_id },
      }),
      invalidatesTags: (_r, _e, a) => scopeTags(a.dataset, a.variant),
    }),

    // A rename, not a re-file: the id stays, so every entry already filed under
    // this category follows the new name without being touched. Correcting a
    // spelling the import invented ("Eartwork") is the common case, and doing it
    // by delete-and-recreate would orphan every entry under the old id.
    //
    // `level` is for the one register that keeps its sub-categories in a table
    // of their own (the basic price list): there, id 3 exists in both tables
    // and the API cannot tell which row is meant. Every other register ignores
    // it — an id identifies a taxonomy row by itself.
    renameDatasetCategory: b.mutation<any, CategoryRef & { name: string }>({
      query: ({ dataset, variant, id, level, name }) => ({
        url: `${base(dataset, true)}/categories/${id}${categoryQuery(variant, level)}`,
        method: "PUT",
        body: { name },
      }),
      invalidatesTags: (_r, _e, a) => scopeTags(a.dataset, a.variant),
    }),

    deleteDatasetCategory: b.mutation<any, CategoryRef>({
      query: ({ dataset, variant, id, level }) => ({
        url: `${base(dataset, true)}/categories/${id}${categoryQuery(variant, level)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, a) => scopeTags(a.dataset, a.variant),
    }),
  }),
});

export const {
  useGetDatasetItemsQuery,
  useGetDatasetFiltersQuery,
  useGetDatasetCategoriesQuery,
  useCreateDatasetEntryMutation,
  useUpdateDatasetEntryMutation,
  useDeleteDatasetEntryMutation,
  useImportDatasetMutation,
  useCreateDatasetCategoryMutation,
  useRenameDatasetCategoryMutation,
  useDeleteDatasetCategoryMutation,
} = datasetApi;
